import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import notificationService from '../../../services/notificationService'; // ← ADD THIS

const MessagesTab = ({ 
  messages, 
  onSendMessage, 
  currentUser,
  caseId,
  caseNumber,
  recipientId,
  /** Destinataires labo additionnels (prothésiste, responsable phase, staff assigné…). */
  additionalRecipientIds = [],
  notifyAdmins = false,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if ((!newMessage?.trim() && attachments?.length === 0) || sending) return;
    
    setSending(true);
    try {
      await onSendMessage({
        content: newMessage,
        attachments: attachments
      });

      const actorId = currentUser?.id ? String(currentUser.id) : null;
      const recipientIds = new Set(
        [recipientId, ...(additionalRecipientIds || [])]
          .filter(Boolean)
          .map(String)
          .filter((id) => id !== actorId)
      );

      const notificationPayload = {
        type: 'info',
        title: 'Nouveau message reçu',
        message: `Vous avez reçu un nouveau message sur le cas ${caseNumber || caseId} de la part de ${currentUser?.name || 'Un utilisateur'}`,
        link: `/case-detail/${caseId}?tab=messages`,
        metadata: {
          caseId,
          caseNumber,
          senderId: currentUser?.id,
          senderName: currentUser?.name,
          messageType: 'case_message',
        },
      };

      for (const userId of recipientIds) {
        await notificationService.createNotification({
          userId,
          ...notificationPayload,
        });
      }

      if (notifyAdmins) {
        await notificationService.notifyAllAdministrators({
          type: 'info',
          title: 'Nouveau message sur un cas',
          message: `Nouveau message sur le cas ${caseNumber || caseId}`,
          link: `/case-detail/${caseId}?tab=messages`,
          metadata: { caseId, caseNumber, senderId: currentUser?.id },
          excludeUserIds: [actorId, ...recipientIds],
        });
      }

      setNewMessage('');
      setAttachments([]);
      
    } catch (error) {
      console.error('Error sending message or notification:', error);
    } finally {
      setSending(false);
    }
  };

  const handleAttachment = () => {
    console.log('Attachment dialog opened');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="flex flex-col h-[600px]">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Messages du dossier</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="MessageSquare" size={16} />
          <span>{messages?.length} messages</span>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => {
          const isCurrentUser = message?.senderId === currentUser?.id;
          
          return (
            <div key={message?.id} className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                {message?.senderInitials}
              </div>
              <div className={`flex-1 max-w-[70%] ${isCurrentUser ? 'items-end' : 'items-start'} flex flex-col`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground">{message?.senderName}</span>
                  <span className="text-xs text-muted-foreground">{formatTime(message?.timestamp)}</span>
                </div>

                <div className={`rounded-lg p-3 ${
                  isCurrentUser 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-foreground'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message?.content}</p>

                  {message?.attachments && message?.attachments?.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message?.attachments?.map((attachment, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-black/10 rounded">
                          <Icon name="Paperclip" size={16} />
                          <span className="text-xs">{attachment?.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {message?.readBy && message?.readBy?.length > 0 && isCurrentUser && (
                  <div className="flex items-center gap-1 mt-1">
                    <Icon name="CheckCheck" size={14} className="text-success" />
                    <span className="text-xs text-muted-foreground">Lu</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="p-4 border-t border-border">
        <div className="block gap-2">
          <Input
            type="text"
            placeholder="Tapez votre message…"
            value={newMessage}
            style={{ height: '100px', maxHeight: '100px', marginBottom: '10px' }}
            onChange={(e) => setNewMessage(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSend()} // Prevent shift+enter from sending
            className="flex-1 h-100"
            disabled={sending} // ← DISABLE DURING SEND
          />
          <Button
            variant="default"
            iconName={sending ? 'Loader' : 'Send'} // ← SHOW LOADER ICON WHILE SENDING
            onClick={handleSend}
            disabled={!newMessage?.trim() && attachments?.length === 0 || sending}
            className={sending ? 'opacity-70 cursor-not-allowed' : ''}
          >
            {sending ? 'Envoi...' : 'Envoyer'} 
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;